import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

// SYSTEM CHECKS (Studio Blueprint 05-C): a status panel in the frame's
// style, green lights and plain sentences, showing only the lines that are
// true where it sits. Each line names a rule the code keeps; check the code
// before adding one (the blueprint: never show what you can't stand behind).
//
//   noChargeBeforeApprove  studios charge at render, after the free preview
//   failedRefunds          a failed render returns its credits
//   cardsToStripe          card details are typed on Stripe's page only
//   editorOnDevice         ChopBox edits on the device; files leave on Save
//   storybookFaces         face crops deleted at approval, photos not kept
//   quickNoCharge          a quick tool charges nothing before the preview
//   quickDeleted           quick-tool files are deleted after 48 hours
//                          (services/quicktools/jobs.js RETENTION_MS)
//   quickNoAccount         no account, no credits, pay per file

export type CheckKey =
  | 'noChargeBeforeApprove'
  | 'failedRefunds'
  | 'cardsToStripe'
  | 'editorOnDevice'
  | 'storybookFaces'
  | 'quickNoCharge'
  | 'quickDeleted'
  | 'quickNoAccount';

export function SystemChecks({ checks, className }: { checks: readonly CheckKey[]; className?: string }) {
  const { t } = useTranslation();
  return (
    <section
      aria-labelledby="system-checks"
      data-testid="system-checks"
      className={cn('mx-auto max-w-3xl rounded-2xl border border-foreground/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.015))] p-5 sm:p-6', className)}
    >
      <h2 id="system-checks" className="mb-4 flex items-center gap-2 font-mono text-[12px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        <span aria-hidden className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.8)]" />
        {t('trust.checks.title')}
      </h2>
      <ul className="space-y-3">
        {checks.map((key) => (
          <li key={key} className="flex items-start gap-3 text-[15px] leading-snug text-foreground">
            <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-400" />
            <span>{t(`trust.checks.${key}`)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
